import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import type React from "react";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import z from "zod";
import { client } from "@/api-client";
import type { FileOutput } from "@/api-types";
import { Header } from "@/components/Header";
import { TrainerRoutes, useNavigator } from "@/routers/routes";
import {
  type QuestionDefinition,
  SurveyContentSchema,
  type SurveyValue,
} from "@/utils/survey-schema";

// SurveyQuestionsEditor component
interface SurveyQuestionsEditorProps {
  content: SurveyContent;
  onChange: (content: SurveyContent) => void;
  onError?: (error: string | null) => void;
}

interface QuestionProps {
  question: QuestionDefinition;
  onUpdate: (id: string, updates: Partial<QuestionDefinition>) => void;
  onDelete: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  isFirst: boolean;
  isLast: boolean;
}

const QuestionEditor: React.FC<QuestionProps> = ({
  question,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) => {
  const { id, label, description, type, required, options = [] } = question;

  const updateField = (field: string, value: SurveyValue) => {
    onUpdate(id, { [field]: value });
  };

  const addOption = (value: string) => {
    if (value.trim()) {
      onUpdate(id, { options: [...(options || []), value.trim()] });
    }
  };

  const removeOption = (index: number) => {
    const newOptions = [...(options || [])];
    newOptions.splice(index, 1);
    onUpdate(id, { options: newOptions });
  };

  const updateOption = (index: number, value: string) => {
    const currentOptions = options || [];
    if (index === currentOptions.length) {
      // If editing the "last" option (which doesn't exist yet), add it
      if (value.trim()) {
        addOption(value);
      }
    } else {
      // Otherwise update existing option
      const newOptions = [...currentOptions];
      newOptions[index] = value;
      onUpdate(id, { options: newOptions });
    }
  };

  const needsOptions =
    type === "MULTIPLE_CHOICE" || type === "MULTIPLE_CHOICE_MULTI";
  const currentOptions = options || [];

  return (
    <Paper p="md" withBorder>
      <Stack>
        <Group>
          <ActionIcon
            variant="subtle"
            onClick={() => onMoveUp(id)}
            disabled={isFirst}
          >
            <IconArrowUp size="1.2rem" />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            onClick={() => onMoveDown(id)}
            disabled={isLast}
          >
            <IconArrowDown size="1.2rem" />
          </ActionIcon>
          <Box style={{ flex: 1 }}>
            <TextInput
              label="Question"
              value={label}
              onChange={(e) => updateField("label", e.target.value)}
              placeholder="Enter question text"
              required
            />
          </Box>
          <ActionIcon color="red" onClick={() => onDelete(id)}>
            <IconTrash size="1.2rem" />
          </ActionIcon>
        </Group>

        <Textarea
          label="Description"
          value={description}
          onChange={(e) => updateField("description", e.target.value)}
          placeholder="Enter additional description or instructions (optional)"
        />

        <Group>
          <Select
            label="Question Type"
            value={type}
            onChange={(value) => value && updateField("type", value)}
            data={[
              { value: "SHORT_TEXT", label: "Short Text" },
              { value: "LONG_TEXT", label: "Long Text" },
              { value: "YES_NO", label: "Yes/No" },
              {
                value: "MULTIPLE_CHOICE",
                label: "Multiple Choice (Single Select)",
              },
              {
                value: "MULTIPLE_CHOICE_MULTI",
                label: "Multiple Choice (Multi Select)",
              },
            ]}
            style={{ flex: 1 }}
          />
          <Checkbox
            label="Required"
            checked={required}
            onChange={(e) => updateField("required", e.currentTarget.checked)}
            mt={30}
          />
        </Group>

        {needsOptions && (
          <Box>
            <Text fw={500} size="sm">
              Options
            </Text>
            <Stack gap="xs">
              {[...currentOptions, ""].map((option, index) => (
                <Group key={index}>
                  <TextInput
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={
                      index === currentOptions.length
                        ? "Add new option"
                        : undefined
                    }
                    style={{ flex: 1 }}
                  />
                  {index < currentOptions.length && (
                    <ActionIcon color="red" onClick={() => removeOption(index)}>
                      <IconTrash size="1rem" />
                    </ActionIcon>
                  )}
                </Group>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export const SurveyQuestionsEditor: React.FC<SurveyQuestionsEditorProps> = ({
  content,
  onChange,
  onError,
}) => {
  const updateContent = (updatedQuestions: QuestionDefinition[]) => {
    try {
      const surveyContent: SurveyContent = {
        version: content.version,
        questions: updatedQuestions,
      };
      onChange(surveyContent);
      /*
      const result = SurveyContentSchema.safeParse(surveyContent);
      
      if (result.success) {
        onChange(surveyContent);
        onError?.(null);
      } else {
        const errors = result.error.errors;
        const errorMessages = errors.map(err => err.message).join(', ');
        onError?.(`Validation error: ${errorMessages}`);
      }*/
    } catch (error) {
      console.error("Error updating survey content:", error);
      onError?.("Failed to update survey content. Please try again.");
    }
  };

  const addQuestion = () => {
    const newQuestion: QuestionDefinition = {
      id: uuidv4(),
      label: "",
      description: "",
      type: "SHORT_TEXT",
      required: false,
      options: null,
    };

    updateContent([...content.questions, newQuestion]);
  };

  const updateQuestion = (id: string, updates: Partial<QuestionDefinition>) => {
    const updatedQuestions = content.questions.map((q) =>
      q.id === id
        ? {
            ...q,
            label: updates.label ?? q.label,
            description: updates.description ?? q.description,
            type: updates.type ?? q.type,
            required: updates.required ?? q.required,
            options:
              updates.options === undefined ? q.options : updates.options,
          }
        : q,
    );
    updateContent(updatedQuestions);
  };

  const deleteQuestion = (id: string) => {
    updateContent(content.questions.filter((q) => q.id !== id));
  };

  const swapQuestions = (
    questions: QuestionDefinition[],
    index1: number,
    index2: number,
  ): QuestionDefinition[] => {
    const result = [...questions];
    const question1 = result[index1];
    const question2 = result[index2];
    if (question1 && question2) {
      result[index1] = question2;
      result[index2] = question1;
    }
    return result;
  };

  const moveQuestionUp = (id: string) => {
    const index = content.questions.findIndex((q) => q.id === id);
    if (index > 0) {
      updateContent(swapQuestions(content.questions, index, index - 1));
    }
  };

  const moveQuestionDown = (id: string) => {
    const index = content.questions.findIndex((q) => q.id === id);
    if (index < content.questions.length - 1) {
      updateContent(swapQuestions(content.questions, index, index + 1));
    }
  };

  return (
    <Stack gap="md">
      <Title order={4}>Survey Questions</Title>

      {content.questions.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text ta="center" color="dimmed">
            No questions added yet. Click the button below to add your first
            question.
          </Text>
        </Paper>
      ) : (
        content.questions.map((question, index) => (
          <QuestionEditor
            key={question.id}
            question={question}
            onUpdate={updateQuestion}
            onDelete={deleteQuestion}
            onMoveUp={moveQuestionUp}
            onMoveDown={moveQuestionDown}
            isFirst={index === 0}
            isLast={index === content.questions.length - 1}
          />
        ))
      )}

      <Button
        leftSection={<IconPlus size="1rem" />}
        onClick={addQuestion}
        fullWidth
      >
        Add Question
      </Button>
    </Stack>
  );
};

// Main SurveyEditorComponent
interface SurveyEditorComponentProps {
  file?: FileOutput;
  isCreating?: boolean;
  folderIdParam: string | null | undefined;
}

interface SurveyContent {
  version: string;
  questions: QuestionDefinition[];
}

export const SurveyEditor: React.FC<SurveyEditorComponentProps> = ({
  file,
  isCreating = false,
  folderIdParam,
}) => {
  const { navigate } = useNavigator();
  const [name, setName] = useState(file?.name ?? "");
  const [description, setDescription] = useState(file?.description ?? "");
  const [content, setContent] = useState<SurveyContent>(() => {
    if (file?.content) {
      return JSON.parse(file.content);
    }
    return {
      version: new Date().toISOString(),
      questions: [],
    };
  });
  const [error, setError] = useState<string | null>(null);

  const parentFolderId = file?.parentFolderId || folderIdParam || null;

  // For surveys, use the survey endpoint
  const createSurveyMutation = client.useMutation(
    "post",
    "/universal/files/survey",
  );
  const updateSurveyMutation = client.useMutation(
    "post",
    "/universal/files/survey/{surveyId}",
  );

  const validateSurveyContent = (contentToValidate: SurveyContent) => {
    try {
      const result = SurveyContentSchema.safeParse(contentToValidate);

      if (!result.success) {
        const errorMessages = z.prettifyError(result.error);
        setError(`Survey validation error: ${errorMessages}`);
        return false;
      }

      return true;
    } catch (_error) {
      setError("Invalid survey content structure");
      return false;
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Survey name is required");
      return;
    }

    // Validate survey content
    if (!validateSurveyContent(content)) {
      return;
    }

    try {
      if (isCreating) {
        await createSurveyMutation.mutateAsync({
          body: {
            name,
            description,
            content,
            parentFolderId,
          },
        });
      } else if (file) {
        await updateSurveyMutation.mutateAsync({
          params: {
            path: {
              surveyId: file.id,
            },
          },
          body: {
            name,
            description,
            content,
            parentFolderId,
          },
        });
      }

      if (folderIdParam && folderIdParam !== "root") {
        navigate({
          path: TrainerRoutes.LIBRARY.FOLDER,
          params: { folderId: folderIdParam },
        });
      } else {
        navigate({ path: TrainerRoutes.LIBRARY.LIST });
      }
    } catch (error) {
      console.error("Error saving survey:", error);
      setError("Failed to save survey. Please try again.");
    }
  };

  const handleContentChange = (newContent: SurveyContent) => {
    setContent(newContent);
    setError(null);
  };

  return (
    <Stack>
      <Header title={isCreating ? "Create Survey" : "Edit Survey"} />

      <form onSubmit={onSubmit}>
        <Stack>
          {error && (
            <Alert color="red" title="Error" icon={<IconAlertCircle />}>
              {error}
            </Alert>
          )}

          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Enter survey name"
          />

          <Textarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autosize
            minRows={3}
            placeholder="Enter description (optional)"
          />

          <SurveyQuestionsEditor
            content={content}
            onChange={handleContentChange}
            onError={setError}
          />

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                if (folderIdParam && folderIdParam !== "root") {
                  navigate({
                    path: TrainerRoutes.LIBRARY.FOLDER,
                    params: { folderId: folderIdParam },
                  });
                } else {
                  navigate({
                    path: TrainerRoutes.LIBRARY.LIST,
                  });
                }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={
                isCreating
                  ? createSurveyMutation.isPending
                  : updateSurveyMutation.isPending
              }
            >
              {isCreating ? "Create" : "Save Changes"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
};
